import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner, Badge, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faGripVertical, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    getRiversamentiBlocks,
    createRiversamentiBlock,
    updateRiversamentiBlock,
    deleteRiversamentiBlock,
    reorderRiversamentiBlocks,
} from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import RiversamentiBlockForm, { PAGINE, TIPI } from '../components/forms/RiversamentiBlockForm';

const etichettaTipo = (t) => TIPI.find((x) => x.valore === t)?.etichetta || t;

// Riga propria e non SortableBlockRow condivisa: qui serve anche la zona,
// che sulla pagina "Chi siamo" non esiste.
const RigaBlocco = ({ blocco, onModifica, onElimina }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: blocco.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        position: 'relative',
    };
    const zonaFissa = ['intestazione', 'faq'].includes(blocco.block_type);

    return (
        <tr ref={setNodeRef} style={style} {...attributes}>
            <td className="text-center align-middle">
                <button {...listeners} style={{ cursor: 'grab', border: 'none', background: 'transparent' }}>
                    <FontAwesomeIcon icon={faGripVertical} />
                </button>
            </td>
            <td className="align-middle">{blocco.title || <span className="text-muted">senza nome</span>}</td>
            <td className="align-middle">{etichettaTipo(blocco.block_type)}</td>
            <td className="align-middle">
                {zonaFissa ? (
                    <span className="text-muted">posizione fissa</span>
                ) : blocco.zona === 'alto' ? 'In alto' : 'In basso'}
            </td>
            <td className="align-middle">
                <Badge bg={blocco.is_active ? 'success' : 'secondary'}>
                    {blocco.is_active ? 'Visibile' : 'Nascosto'}
                </Badge>
            </td>
            <td className="align-middle text-nowrap">
                <Button variant="warning" size="sm" className="me-2" onClick={() => onModifica(blocco)}>
                    <FontAwesomeIcon icon={faEdit} />
                </Button>
                <Button variant="danger" size="sm" onClick={() => onElimina(blocco)}>
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </td>
        </tr>
    );
};

const ContenutiRiversamenti = () => {
    const [pagina, setPagina] = useState('home');
    const [blocchi, setBlocchi] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [daEliminare, setDaEliminare] = useState(null);
    const [inModifica, setInModifica] = useState(null);
    const [mostraForm, setMostraForm] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const carica = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getRiversamentiBlocks(pagina);
            setBlocchi(res.data || []);
        } catch (err) {
            setError('Impossibile caricare i contenuti del sito riversamenti.');
        } finally {
            setLoading(false);
        }
    }, [pagina]);

    useEffect(() => { carica(); }, [carica]);

    const salva = async (dati) => {
        try {
            if (inModifica) {
                await updateRiversamentiBlock(inModifica.id, dati);
                toast.success('Blocco aggiornato.');
            } else {
                await createRiversamentiBlock({ ...dati, order: blocchi.length });
                toast.success('Blocco creato.');
            }
            setMostraForm(false);
            setInModifica(null);
            carica();
        } catch (err) {
            toast.error('Errore nel salvataggio del blocco.');
        }
    };

    const confermaEliminazione = async () => {
        if (!daEliminare) return;
        try {
            await deleteRiversamentiBlock(daEliminare.id);
            toast.success('Blocco eliminato.');
            carica();
        } catch (err) {
            toast.error("Errore durante l'eliminazione.");
        } finally {
            setDaEliminare(null);
        }
    };

    const fineTrascinamento = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const vecchi = blocchi;
        const nuovi = arrayMove(
            blocchi,
            blocchi.findIndex((b) => b.id === active.id),
            blocchi.findIndex((b) => b.id === over.id)
        );
        setBlocchi(nuovi);
        try {
            await reorderRiversamentiBlocks(nuovi.map((b) => b.id));
            toast.success('Ordine salvato.');
        } catch (err) {
            toast.error("Errore nel salvataggio dell'ordine.");
            setBlocchi(vecchi);
        }
    };

    const urlPagina = pagina === 'home' ? '/riversamenti' : `/riversamenti/${pagina}`;

    return (
        <Container fluid>
            <Row className="mb-3 align-items-center">
                <Col>
                    <h2 className="mb-0">Contenuti sito riversamenti</h2>
                    <p className="text-muted mb-0">
                        Testi e foto di modenariversamenti.it. Quello che non tocchi resta come adesso.
                    </p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="outline-secondary"
                        className="me-2"
                        href={urlPagina}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <FontAwesomeIcon icon={faUpRightFromSquare} /> Vedi la pagina
                    </Button>
                    <Button variant="primary" onClick={() => { setInModifica(null); setMostraForm(true); }}>
                        <FontAwesomeIcon icon={faPlus} /> Nuovo blocco
                    </Button>
                </Col>
            </Row>

            <Nav variant="tabs" activeKey={pagina} onSelect={(k) => setPagina(k)} className="mb-3">
                {PAGINE.map((p) => (
                    <Nav.Item key={p.valore}>
                        <Nav.Link eventKey={p.valore}>{p.etichetta}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card>
                <Card.Body>
                    {loading ? (
                        <div className="text-center p-4"><Spinner animation="border" /></div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={fineTrascinamento}>
                            <SortableContext items={blocchi.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                                <Table striped bordered hover responsive className="mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 50 }}></th>
                                            <th>Nome</th>
                                            <th>Tipo</th>
                                            <th>Posizione</th>
                                            <th>Stato</th>
                                            <th style={{ width: 120 }}>Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blocchi.map((b) => (
                                            <RigaBlocco
                                                key={b.id}
                                                blocco={b}
                                                onModifica={(x) => { setInModifica(x); setMostraForm(true); }}
                                                onElimina={setDaEliminare}
                                            />
                                        ))}
                                    </tbody>
                                </Table>
                            </SortableContext>
                        </DndContext>
                    )}
                    {!loading && blocchi.length === 0 && (
                        <div className="text-center text-muted p-4">
                            Nessun blocco su questa pagina: il sito mostra i testi predefiniti.
                            Aggiungine uno per cambiarli o per inserire foto e sezioni nuove.
                        </div>
                    )}
                </Card.Body>
            </Card>

            <ConfirmModal
                show={!!daEliminare}
                handleClose={() => setDaEliminare(null)}
                handleConfirm={confermaEliminazione}
                title="Conferma eliminazione"
                body={`Eliminare il blocco "${daEliminare?.title || 'senza nome'}"?`}
            />

            <RiversamentiBlockForm
                show={mostraForm}
                onHide={() => { setMostraForm(false); setInModifica(null); }}
                onSubmit={salva}
                initialData={inModifica}
                paginaCorrente={pagina}
            />
        </Container>
    );
};

export default ContenutiRiversamenti;
